import { useSegments } from '../../hooks/useSegments'
import { usePlanItems } from '../../hooks/usePlanItems'
import { calculateNutritionTotals, calculatePerHourRates } from '../../lib/nutrition'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Progress } from '../ui/progress'

interface NutritionSummaryProps {
  runId: string
}

export function NutritionSummary({ runId }: NutritionSummaryProps) {
  const { data: segments } = useSegments(runId)
  const { data: planItems } = usePlanItems(runId)

  if (!segments || !planItems) {
    return <div className="text-center py-4 text-neutral-600">Loading nutrition data...</div>
  }

  const totals = calculateNutritionTotals(planItems)
  const perHourRates = calculatePerHourRates(segments, planItems)

  // Default targets (should come from calibration profile)
  const targets = {
    carbs_g_per_h: { low: 60, high: 90 },
    fluids_ml_per_h: { low: 500, high: 750 },
    sodium_mg_per_h: { low: 300, high: 800 }
  }

  const getProgressColor = (value: number, target: { low: number; high: number }) => {
    if (value < target.low) return 'text-amber-600'
    if (value > target.high) return 'text-accent-600'
    return 'text-emerald-600'
  }

  const getProgressValue = (value: number, target: { low: number; high: number }) => {
    const optimal = (target.low + target.high) / 2
    return Math.min((value / optimal) * 100, 100)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-orange-800">Carbohydrates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-end">
              <span className="text-3xl font-bold text-orange-900">{totals.carbs_g}g</span>
              <span className={`text-sm font-medium ${getProgressColor(perHourRates.carbs_g, targets.carbs_g_per_h)}`}>
                {perHourRates.carbs_g.toFixed(1)}g/h
              </span>
            </div>
            <Progress
              value={getProgressValue(perHourRates.carbs_g, targets.carbs_g_per_h)}
              className="h-3"
            />
            <p className="text-xs text-orange-700 font-medium">
              Target: {targets.carbs_g_per_h.low}-{targets.carbs_g_per_h.high}g/h
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-blue-800">Fluids</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-end">
              <span className="text-3xl font-bold text-blue-900">{totals.fluids_ml}ml</span>
              <span className={`text-sm font-medium ${getProgressColor(perHourRates.fluids_ml, targets.fluids_ml_per_h)}`}>
                {perHourRates.fluids_ml.toFixed(0)}ml/h
              </span>
            </div>
            <Progress
              value={getProgressValue(perHourRates.fluids_ml, targets.fluids_ml_per_h)}
              className="h-3"
            />
            <p className="text-xs text-blue-700 font-medium">
              Target: {targets.fluids_ml_per_h.low}-{targets.fluids_ml_per_h.high}ml/h
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-purple-800">Sodium</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-end">
              <span className="text-3xl font-bold text-purple-900">{totals.sodium_mg}mg</span>
              <span className={`text-sm font-medium ${getProgressColor(perHourRates.sodium_mg, targets.sodium_mg_per_h)}`}>
                {perHourRates.sodium_mg.toFixed(0)}mg/h
              </span>
            </div>
            <Progress
              value={getProgressValue(perHourRates.sodium_mg, targets.sodium_mg_per_h)}
              className="h-3"
            />
            <p className="text-xs text-purple-700 font-medium">
              Target: {targets.sodium_mg_per_h.low}-{targets.sodium_mg_per_h.high}mg/h
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}